import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import {
  Board,
  Move,
  IMove,
  IPiece,
  Position,
  PieceType,
} from "..\/..\/..\/..\/types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

/**
 * Priority 8: Two Turn Defence Stay
 *
 * PROACTIVE DEFENSE: Prevents moves that would create NEW 2-turn enemy threats
 *
 * Unlike Two Turn King Defence (reactive), this priority:
 * 1. Simulates each of our possible moves
 * 2. Checks if that move creates NEW 2-turn enemy threats
 * 3. Filters out moves that abandon defensive positions
 *
 * Critical for preventing defensive abandonment like Wolf leaving [7,1] and exposing [5,0]
 */
export class TwoTurnDefenceStayPriority extends BaseDecisionPriority {
  readonly priority = 11;
  readonly name = "Two Turn Defence Stay";
  readonly description =
    "PROACTIVE: Prevents moves that create new 2-turn enemy threats by abandoning defensive positions";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    return true;
  }

  analyze(context: DecisionContext): DecisionResult {
    this.analysisLogs = [];

    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;
    const enemyColor = isPlayerTurn ? "black" : "white";

    this.addLog(
      `🛡️ PROACTIVE DEFENSE: Checking for moves that create new 2-turn enemy threats`
    );

    // STEP 1: Find current enemy 2-turn threats (baseline)
    const currentThreats = this.findEnemyTwoTurnThreats(gameState, enemyColor);
    this.addLog(
      `📊 Current baseline: ${currentThreats.length} existing 2-turn enemy threats`
    );

    // STEP 2: Filter out moves that create NEW threats
    const safeMoves: MoveInfo[] = [];
    const dangerousMoves: MoveInfo[] = [];

    for (const moveInfo of context.allPossibleMoves) {
      if (
        this.doesMoveCreateNewThreats(
          moveInfo,
          gameState,
          currentThreats.length,
          enemyColor
        )
      ) {
        dangerousMoves.push(moveInfo);
        this.addLog(
          `❌ ABANDONMENT: [${moveInfo.fromPosition.row},${moveInfo.fromPosition.col}] → [${moveInfo.move.position.row},${moveInfo.move.position.col}] creates new enemy threats`
        );
      } else {
        safeMoves.push(moveInfo);
      }
    }

    if (dangerousMoves.length === 0) {
      this.addLog(
        `✅ All ${context.allPossibleMoves.length} moves maintain defensive integrity`
      );
      return {
        shouldEarlyExit: false,
        reasoning: "No moves create new 2-turn threats",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    if (safeMoves.length === 0) {
      this.addLog(
        `💀 CRITICAL: All moves create new threats! Forced defensive abandonment.`
      );
      return {
        shouldEarlyExit: false,
        reasoning:
          "WARNING: All moves create new 2-turn threats - forced abandonment",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    this.addLog(
      `🛡️ Filtered out ${dangerousMoves.length} threat-creating moves, keeping ${safeMoves.length} safe moves`
    );

    // Log the dangerous moves that were filtered
    for (const dangerous of dangerousMoves) {
      this.addLog(
        `❌ FILTERED: [${dangerous.fromPosition.row},${dangerous.fromPosition.col}] → [${dangerous.move.position.row},${dangerous.move.position.col}]`
      );
    }

    return this.createFilterResult(
      safeMoves,
      `PROACTIVE DEFENSE: Filtered out ${dangerousMoves.length} moves that create new 2-turn threats`,
      {
        dangerousMoves: dangerousMoves.length,
        safeMoves: safeMoves.length,
        currentThreats: currentThreats.length,
        logs: this.analysisLogs,
      }
    );
  }

  /**
   * Check if a move creates NEW 2-turn enemy threats
   */
  private doesMoveCreateNewThreats(
    moveInfo: MoveInfo,
    gameState: any,
    baselineThreats: number,
    enemyColor: "white" | "black"
  ): boolean {
    try {
      // Simulate our move
      const stateAfterMove = this.simulateMove(gameState, moveInfo);
      if (!stateAfterMove) return true; // Assume dangerous if simulation fails

      // Find enemy threats after our move
      const newThreats = this.findEnemyTwoTurnThreats(
        stateAfterMove,
        enemyColor
      );

      // If more threats exist after our move = dangerous abandonment
      return newThreats.length > baselineThreats;
    } catch (error) {
      this.addLog(`❌ Error analyzing move: ${error}`);
      return true; // Assume dangerous if analysis fails
    }
  }

  /**
   * Find all enemy king 2-turn winning paths
   * COPIED from TwoTurnKingDefencePriority with same sophisticated logic
   */
  private findEnemyTwoTurnThreats(
    gameState: any,
    enemyColor: "white" | "black"
  ): Array<{ firstMove: MoveInfo; secondMove: MoveInfo }> {
    const threats: Array<{ firstMove: MoveInfo; secondMove: MoveInfo }> = [];
    const victoryRow = enemyColor === "white" ? 7 : 0;

    // Find enemy king
    const enemyKing = this.findKing(gameState.board, enemyColor);
    if (!enemyKing) return threats;

    // Generate enemy king's first moves
    const originalTurn = gameState.isLightTurn;
    gameState.isLightTurn = enemyColor === "white";

    try {
      const moveService = MCTSMoveGenerationService.getInstance();
      const enemyFirstMoves = moveService
        .getAllMoves(gameState)
        .filter(
          (move) =>
            move.fromPosition.row === enemyKing.row &&
            move.fromPosition.col === enemyKing.col
        );

      // For each first move, check if second move can win
      for (const firstMove of enemyFirstMoves) {
        // Skip if first move already wins (that's 1-turn, not 2-turn)
        if (firstMove.move.position.row === victoryRow) continue;

        // CRITICAL: Skip moves to defended squares - king won't suicide!
        if (
          this.isSquareDefendedAgainstKing(
            gameState,
            firstMove.move.position,
            enemyColor
          )
        ) {
          continue; // Skip defended squares
        }

        // Simulate first move
        const stateAfterFirst = this.simulateMove(gameState, firstMove);
        if (!stateAfterFirst) continue;

        // Our turn would be skipped in this simulation - go straight to enemy's second move
        stateAfterFirst.isLightTurn = enemyColor === "white";

        // Generate enemy's second moves from new position
        const enemySecondMoves = moveService
          .getAllMoves(stateAfterFirst)
          .filter(
            (move) =>
              move.fromPosition.row === firstMove.move.position.row &&
              move.fromPosition.col === firstMove.move.position.col
          );

        // Check if any second move wins
        for (const secondMove of enemySecondMoves) {
          if (secondMove.move.position.row === victoryRow) {
            // VICTORY SQUARE FIX: Victory squares are NEVER defended - game ends immediately!
            threats.push({ firstMove, secondMove });
          }
        }
      }
    } finally {
      gameState.isLightTurn = originalTurn;
    }

    return threats;
  }

  /**
   * Check if a square is defended against enemy king
   * COPIED from TwoTurnKingDefencePriority
   */
  private isSquareDefendedAgainstKing(
    gameState: any,
    position: Position,
    enemyColor: "white" | "black"
  ): boolean {
    try {
      // Check if there's already one of our pieces there
      const existingPiece = this.getPieceAt(gameState.board, position);
      if (existingPiece && existingPiece.isLight !== (enemyColor === "white")) {
        // Our piece is already there - check if it's defended
        return this.isPieceDefendedAt(
          gameState,
          position,
          existingPiece.isLight
        );
      }

      // Empty square - simulate enemy king moving there
      const testState = cloneFullGameState(gameState);

      // Find enemy king
      const enemyKing = this.findKing(testState.board, enemyColor);
      if (!enemyKing) return false;

      // Simulate king moving to the position
      const kingPiece = this.getPieceAt(testState.board, enemyKing);
      if (!kingPiece) return false;

      // Move king to position
      testState.board[enemyKing.row][enemyKing.col].Pieces = [];
      testState.board[position.row][position.col].Pieces = [kingPiece];

      // Switch to our turn
      testState.isLightTurn = !(enemyColor === "white");

      // Check if we can capture the king at this position
      const moveService = MCTSMoveGenerationService.getInstance();
      const ourMoves = moveService.getAllMoves(testState);

      for (const move of ourMoves) {
        if (
          move.move.position.row === position.row &&
          move.move.position.col === position.col
        ) {
          // We can capture the king = defended square!
          return true;
        }
      }

      return false; // Square not defended
    } catch (error) {
      return false; // Assume not defended if simulation fails
    }
  }

  /**
   * Check if a piece at given position is defended
   * COPIED from TwoTurnKingDefencePriority
   */
  private isPieceDefendedAt(
    gameState: any,
    position: Position,
    isOurPieceLight: boolean
  ): boolean {
    try {
      // Simulate enemy king capturing our piece at this position
      const testState = cloneFullGameState(gameState);

      // Find enemy king
      const enemyColor = isOurPieceLight ? "black" : "white";
      const enemyKing = this.findKing(testState.board, enemyColor);
      if (!enemyKing) return false;

      // Simulate king capturing our piece
      const kingPiece = this.getPieceAt(testState.board, enemyKing);
      if (!kingPiece) return false;

      // Move king to capture position
      testState.board[enemyKing.row][enemyKing.col].Pieces = [];
      testState.board[position.row][position.col].Pieces = [kingPiece];

      // Switch to our turn
      testState.isLightTurn = isOurPieceLight;

      // Check if we can capture the king at the position
      const moveService = MCTSMoveGenerationService.getInstance();
      const ourMoves = moveService.getAllMoves(testState);

      for (const move of ourMoves) {
        if (
          move.move.position.row === position.row &&
          move.move.position.col === position.col
        ) {
          // We can recapture the king = defended piece!
          return true;
        }
      }

      return false; // We cannot recapture = undefended
    } catch (error) {
      return false; // Assume undefended if simulation fails
    }
  }

  private simulateMove(gameState: any, moveInfo: MoveInfo): any | null {
    try {
      const testState = cloneFullGameState(gameState);
      const piece = getPieceFromPosition(testState, moveInfo.fromPosition);
      if (!piece) return null;

      applyMoveWithSnakeRespawn(
        testState,
        moveInfo.fromPosition,
        moveInfo.move,
        piece
      );

      return testState;
    } catch (error) {
      return null;
    }
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  private findKing(board: any[][], color: "white" | "black"): Position | null {
    const isLight = color === "white";

    for (let row = 0; row < board.length; row++) {
      for (let col = 0; col < board[row].length; col++) {
        const square = board[row][col];
        if (square.Pieces && square.Pieces.length > 0) {
          const piece = square.Pieces[0];
          if (piece.isLight === isLight && piece.isKing) {
            return { row, col };
          }
        }
      }
    }
    return null;
  }

  private getPieceAt(board: any[][], position: Position): IPiece | null {
    if (
      position.row < 0 ||
      position.row >= board.length ||
      position.col < 0 ||
      position.col >= board[position.row].length
    ) {
      return null;
    }

    const square = board[position.row][position.col];
    if (!square || !square.Pieces || square.Pieces.length === 0) {
      return null;
    }

    return square.Pieces[0];
  }
}

